<template>
    <q-page class="full-width q-px-md-md q-px-sm-sm">
        <h3 class="text-h3 text-center" style="color:rgb(39 170 225);">My Domains</h3>

        <div class="row q-mb-md">
            <q-banner v-if="soonExpiredCount"
                      inline-actions rounded
                      class="bg-amber-13 text-white text-justify col-10 offset-1 col-md-3">
                <template v-slot:avatar>
                    <q-icon name="fa-solid fa-triangle-exclamation" class="self-center full-height"/>
                </template>
                <p class="text-h7 q-mb-none">
                    You have {{ soonExpiredCount }} Domain(s) that will expire soon
                </p>
            </q-banner>
        </div>
        <div class="row">
            <q-table
                :columns="columns"
                :rows="subDomains"
                separator="none"
                class="col-12 col-md-10 offset-md-1"
                row-key="id"
                :visible-columns="visibleColumns"
                v-model:pagination="pagination"
                :filter="search"
                :filter-method="tableFilter"
                :binary-state-sort="true"
                :dense="quasar.screen.lt.md">

                <template v-slot:top-left class="full-width">
                    <q-input dense outlined clearable
                             debounce="300"
                             clear-icon="fa-solid fa-xmark"
                             v-model="search"
                             placeholder="Search"
                             type="search">
                        <template v-slot:append>
                            <q-icon name="fa-solid fa-magnifying-glass"/>
                        </template>
                    </q-input>
                    <div v-if="isMobile" class="column full-width q-my-md">
                        <q-select
                            v-model="sortColumn"
                            label="Sort by"
                            class="col-6"
                            behavior="menu"
                            map-options
                            emit-value
                            dense
                            :options="columns.filter(i=> i.sortable).map((i) => {return {label: i.label,value: i.name}})"
                            @update:model-value="onMobileSortChange"
                        />
                        <q-select
                            v-model="sortDescending"
                            label="Sort direction"
                            class="col-5 offset-1"
                            behavior="menu"
                            map-options
                            emit-value
                            dense
                            :options="[{label: 'Ascending',value:false},{label: 'Descending',value:true}]"
                            @update:model-value="onMobileSortDirectionChange"
                        />
                    </div>
                </template>

                <template v-slot:top-right class="full-width">
                    <AddSubdomain inline @added:subdomain="subdomainAdded"/>
                </template>

                <template v-slot:header="props">
                    <q-tr :props="props">
                        <q-th v-if="isMobile" auto-width/>
                        <q-th v-for="col in props.cols" :props="props" :key="col.name">{{ col.label }}</q-th>
                    </q-tr>
                </template>

                <template v-slot:body="props">
                    <q-tr :props="props" :class="{'expanded': props.expand, 'expires-soon': props.row.is_expires_soon}">
                        <q-td v-if="isMobile">
                            <q-btn size="sm" round dense
                                   @click="props.expand = !props.expand"
                                   :icon="props.expand ? 'fa-solid fa-caret-up':'fa-solid fa-caret-down'"/>
                        </q-td>
                        <q-td key="name" :props="props" class="text-left"><p>{{ props.row.name.toUpperCase() }}</p>
                        </q-td>
                        <q-td key="memo_link_count" :props="props" class="text-left"><p>{{
                                props.row.memo_link_count
                            }}</p>
                        </q-td>
                        <q-td key="created_at" :props="props">
                            <br/>
                            {{ date.formatDate(new Date(props.row.created_at), 'DD/MM/YYYY') }}
                        </q-td>
                        <q-td key="expires_at" :props="props">
                            <br/>
                            {{
                                props.row.expires_at ? date.formatDate(new Date(props.row.expires_at), 'DD/MM/YYYY') : 'Never'
                            }}
                        </q-td>
                        <q-td key="action" :props="props">
                            <q-btn v-if="allowToExtendExpirationDate(props.row)"
                                   class="text-primary q-mr-sm"
                                   flat dense
                                   icon="fa-solid fa-clock-rotate-left"
                                   title="Extend expiration date"
                                   @click="showExtendExpirationDialog(props.row)"/>
                            <q-btn v-else
                                   class="text-grey"
                                   flat dense
                                   align="left"
                                   icon="fa-solid fa-lock"
                                   title="You can extend the expiration date only 3 weeks before">
                                <q-tooltip :target="true" class="bg-warning" anchor="top middle" self="center middle">
                                    You can extend the expiration date only {{ allowExtendMemeLinkBefore }} days before
                                </q-tooltip>
                            </q-btn>
                        </q-td>
                    </q-tr>

                    <q-tr v-if="props.expand && isMobile" :props="props" class="bg-grey-2"
                          :class="{'expires-soon': props.row.is_expires_soon}">
                        <q-td colspan="100%">
                            <div class="row q-mb-md q-pl-md">
                                <div class="col-4"><strong>{{
                                        columns.find(item => item.name === 'memo_link_count').label
                                    }}</strong>
                                </div>
                                <div class="col-8">{{ props.row.memo_link_count }}</div>
                            </div>
                            <div class="row q-mb-md q-pl-md">
                                <div class="col-4"><strong>{{
                                        columns.find(item => item.name === 'created_at').label
                                    }}</strong>
                                </div>
                                <div class="col-8">{{
                                        date.formatDate(new Date(props.row.created_at), 'DD/MM/YYYY')
                                    }}
                                </div>
                            </div>
                            <div class="row q-mb-md q-pl-md">
                                <div class="col-4"><strong>{{
                                        columns.find(item => item.name === 'expires_at').label
                                    }}</strong>
                                </div>
                                <div class="col-8">{{
                                        date.formatDate(new Date(props.row.expires_at), 'DD/MM/YYYY')
                                    }}
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-2 offset-4">
                                    <q-btn
                                        v-if="allowToExtendExpirationDate(props.row)"
                                        class="text-primary"
                                        flat dense
                                        align="left"
                                        icon="fa-solid fa-clock-rotate-left fa-xs"
                                        title="Extend expiration date"
                                        @click="showExtendExpirationDialog(props.row)"/>
                                    <q-btn v-else
                                           class="text-grey"
                                           flat dense
                                           align="left"
                                           icon="fa-solid fa-lock"
                                           :title="`You can extend the expiration date only ${allowToExtendExpirationDate} days before`">
                                        <q-tooltip :target="true" class="bg-warning" anchor="top middle"
                                                   self="center middle">
                                            You can extend the expiration date only <br/>
                                            <strong>{{ allowExtendMemeLinkBefore }} days before</strong>
                                        </q-tooltip>
                                    </q-btn>
                                </div>
                            </div>
                        </q-td>
                    </q-tr>
                </template>

                <template v-slot:no-data="{ icon, message, filter }">
                    <div class="full-width row flex-center text-warning q-gutter-sm ">
                    <span>
            Well this is sad... {{ message }}
          </span>
                        <q-icon size="2em" :name="filter ? 'fa-regular fa-face-sad-tear' : icon"/>
                    </div>
                </template>
            </q-table>
        </div>
    </q-page>
</template>

<script setup>
import { date, useQuasar } from 'quasar'
import { event } from 'vue-gtag'
import { useSubDomainService } from '../../../services/subDomainService'
import { computed, inject, onMounted, ref, watch } from 'vue'
import ConfirmSubdomainDelete from '../../includes/dialogs/ConfirmSubdomainDeleteDialog'
import ExpirationDateExtendDialog from '../../includes/dialogs/ExpirationDateExtendDialog.vue'
import { useStore } from 'vuex'
import AddSubdomain from '../../includes/AddSubdomain.vue'

const quasar = useQuasar()
const subdomainService = useSubDomainService()
const allowExtendMemeLinkBefore = inject('allowExtendXDaysBefore')
const store = useStore()

const pagination = ref({
    sortBy: 'name',
    descending: true,
})
const columns = [
    {
        name: 'name',
        field: 'name',
        required: true,
        sortable: true,
        label: 'Domain',
        align: 'left',
    },
    {
        name: 'memo_link_count',
        field: 'memo_link_count',
        label: 'Memo Link count',
        align: 'left',
        required: true,
        sortable: true,
        sort: (a, b) => a - b,
    },
    {
        name: 'created_at',
        field: 'created_at',
        label: 'Creation date',
        align: 'left',
        sortable: true,
        sort: (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    },
    {
        name: 'expires_at',
        field: 'expires_at',
        label: 'Expiration date',
        align: 'left',
        sortable: true,
        sort: (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    },
    {
        name: 'action',
        label: null,
        align: 'center',
    },
]

const search = ref('')
const sortColumn = ref(pagination.value.sortBy)
const sortDescending = ref(pagination.value.descending)
const subDomains = ref([])

const isMobile = computed(() => quasar.screen.width <= 750)
const visibleColumns = ref((isMobile.value ? [] : columns.filter(item => item.name !== 'name').map(item => item.name)))
const soonExpiredCount = computed(() => subDomains.value.filter(v => v?.is_expires_soon).length)

onMounted(async () => await fetchData())

watch(isMobile, (value) => {
    if (value) {
        visibleColumns.value = []
    } else {
        visibleColumns.value = columns.filter(item => item.name !== 'name').map(item => item.name)
    }
})

async function fetchData () {
    quasar.loading.show()
    subdomainService
        .getAll()
        .then(data => subDomains.value = data)
        .finally(() => quasar.loading.hide())
}

async function extendSubdomain (subdomain) {
    quasar.loading.show()

    subdomainService
        .extendExpirationDate(subdomain.id)
        .then(data => {
            if (data?.name === 'AxiosError') {
                quasar.notify({
                    progress: true,
                    message: data.response.data.errors[0],
                    type: 'negative',
                    position: 'top',
                    multiLine: true
                })
            } else {
                const subdomain = data.data.data

                subDomains.value = subDomains.value.map(item => {
                    if (item.id === subdomain.id) {
                        item = subdomain
                    }
                    return item
                })
                quasar.notify({
                    position: 'top',
                    type: 'positive',
                    message: `Domain "${subdomain.name.toUpperCase()}" expiration date has been extended till ${date.formatDate(new Date(subdomain.expires_at), 'DD/MM/YYYY')}`
                })
                event('subdomain-word-extend-expiration-date', { method: 'Google' })
            }
        })
        .catch(() => {
            quasar.notify({
                progress: true,
                message: 'An error occurred',
                type: 'negative',
                position: 'top',
                multiLine: true
            })
        })
        .finally(() => quasar.loading.hide())
}

function allowToExtendExpirationDate (subdomain) {
    if (!subdomain.expires_at) {
        return false
    }

    const expirationDate = new Date(subdomain.expires_at)
    const now = new Date(new Date().toUTCString())
    const diff = date.getDateDiff(expirationDate, now, 'days')

    return diff <= allowExtendMemeLinkBefore
}

async function confirmDelete (subdomain) {
    quasar.dialog({
        component: ConfirmSubdomainDelete,
        componentProps: {
            subdomain: subdomain.name.toUpperCase(),
        }
    }).onOk(async () => {
        quasar.loading.show()

        subdomainService
            .delete(subdomain.id)
            .then((response) => {
                if (response?.name !== 'AxiosError') {
                    subDomains.value = subDomains.value.filter(item => item.id !== subdomain.id)

                    quasar.notify({
                        position: 'top',
                        type: 'positive',
                        message: `Domain "${subdomain.name.toUpperCase()}" has been deleted`
                    })
                    event('subdomain-delete', { method: 'Google' })
                } else {
                    quasar.notify({
                        progress: true,
                        message: response?.response?.data?.errors?.[0] ?? 'Error while removing Domain',
                        type: 'negative',
                        position: 'top',
                    })
                }
            })
            .finally(() => quasar.loading.hide())
    })
}

function tableFilter (rows, term) {
    term = term.trim().replaceAll(' ', '').toLowerCase()
    return rows.filter(row => row.name.includes(term)
        || row.created_at.includes(term)
        || date.formatDate(new Date(row.created_at), 'DD/MM/YYYY').includes(term))
}

function onMobileSortChange (value) {
    pagination.value.sortBy = value
}

function onMobileSortDirectionChange (val) {
    pagination.value.descending = val
}

async function showExtendExpirationDialog (subdomain) {
    quasar.dialog({
        component: ExpirationDateExtendDialog,
        componentProps: {
            type: 'subdomain',
            entity: subdomain,
        }
    }).onOk(async () => extendSubdomain(subdomain))
}

function subdomainAdded(subdomain) {
    subDomains.value.push(subdomain);
}

</script>

<style scoped>
table.q-table tr.expires-soon {
    color: red;
    font-weight: bold;
}
</style>
